import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useBanner } from '../contexts/BannerContext';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const location = useLocation();
  const { isBannerVisible, bannerHeight } = useBanner();
  
  // Generate breadcrumb items from current path if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (items) return items;
    
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', path: '/' }
    ];
    
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Service-specific naming
      if (segment === 'services' && index === pathSegments.length - 1) {
        breadcrumbs.push({ label: 'Services', path: currentPath });
      } else if (segment === 'services' && pathSegments[index + 1]) {
        breadcrumbs.push({ label: 'Services', path: '/services' });
      } else if (pathSegments[index - 1] === 'services') {
        const serviceNames: Record<string, string> = {
          'cognitive-reach': 'Cognitive Reach',
          'cognitive-build': 'Cognitive Build',
          'cognitive-intelligence': 'Cognitive Intelligence',
          'cognitive-cloud': 'Cognitive Cloud',
          'cognitive-iot': 'Cognitive IoT',
        };
        breadcrumbs.push({
          label: serviceNames[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          path: currentPath
        });
      } else {
        // Standard page naming
        const pageNames: Record<string, string> = {
          'about': 'About',
          'contact': 'Contact',
          'success-stories': 'Success Stories',
          'terms': 'Terms of Service',
          'privacy': 'Privacy Policy'
        };
        breadcrumbs.push({ 
          label: pageNames[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), 
          path: currentPath 
        });
      }
    });
    
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  
  // Don't show breadcrumbs on home page
  if (location.pathname === '/') return null;

  return (
    <nav 
      aria-label="Breadcrumb" 
      className="fixed w-full z-30 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-b border-gray-200 dark:border-blue-500/20 shadow-lg"
      style={{ 
        top: isBannerVisible ? `${bannerHeight + 64}px` : '64px'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="px-3 sm:px-4 py-2">
          <ol 
            className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm overflow-x-auto"
            itemScope 
            itemType="https://schema.org/BreadcrumbList"
          >
          {breadcrumbs.map((item, index) => (
            <li 
              key={item.path} 
              className="flex items-center flex-shrink-0"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              {index > 0 && (
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 dark:text-gray-500 mx-1 sm:mx-2 flex-shrink-0" />
              )}
              {index === breadcrumbs.length - 1 ? (
                <span 
                  className="text-blue-600 dark:text-blue-400 font-semibold flex items-center px-1.5 sm:px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-md whitespace-nowrap"
                  itemProp="name"
                >
                  {index === 0 && <Home className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 flex-shrink-0" />}
                  <span className="truncate max-w-[120px] sm:max-w-none">{item.label}</span>
                  <meta itemProp="position" content={(index + 1).toString()} />
                </span>
              ) : (
                <Link 
                  to={item.path}
                  className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-all duration-200 flex items-center px-1.5 sm:px-2 py-1 rounded-md whitespace-nowrap"
                  itemProp="item"
                >
                  {index === 0 && <Home className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 flex-shrink-0" />}
                  <span itemProp="name" className="truncate max-w-[80px] sm:max-w-none">{item.label}</span>
                  <meta itemProp="position" content={(index + 1).toString()} />
                </Link>
              )}
            </li>
          ))}
          </ol>
        </div>
      </div>
    </nav>
  );
};